import { Component } from '@theme/component';
import { morph, MORPH_OPTIONS } from '@theme/morph';
import { OverflowList } from '@theme/overflow-list';
import { yieldToMainThread, getViewParameterValue, ResizeNotifier, closeConfirmedDisclosure } from '@theme/utilities';
import { ProductSelectEvent } from '@shopify/events';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets - The fieldset elements.
 * @property {HTMLElement} [overflowList] - The overflow list element.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #intentPreloadTimeout;

  /** @type {{ url: string, controller: AbortController, promise: Promise<string> } | undefined} */
  #variantResponsePreload;

  /** @type {Promise<void> | undefined} */
  #selectionUpdate;

  #disclosureArrowActive = false;

  #resizeObserver = new ResizeNotifier(() => this.updateVariantPickerCss());

  connectedCallback() {
    super.connectedCallback();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      const initialCheckedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    this.addEventListener('keydown', this.#handleDisclosureKeydown);
    this.addEventListener('keyup', this.#resetDisclosureArrow);
    this.addEventListener('pointerdown', this.#resetDisclosureArrow);
    this.addEventListener('click', this.#handleDisclosureClick);
    this.addEventListener('focusout', this.#handleDisclosureFocusout);
    this.addEventListener('focusin', this.#handlePreloadIntent);
    this.addEventListener('pointerover', this.#handlePreloadIntent);
    this.addEventListener('pointerdown', this.#handlePreloadIntent);
    this.#resizeObserver.observe(this);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver.disconnect();
    this.removeEventListener('keydown', this.#handleDisclosureKeydown);
    this.removeEventListener('keyup', this.#resetDisclosureArrow);
    this.removeEventListener('pointerdown', this.#resetDisclosureArrow);
    this.removeEventListener('click', this.#handleDisclosureClick);
    this.removeEventListener('focusout', this.#handleDisclosureFocusout);
    this.removeEventListener('focusin', this.#handlePreloadIntent);
    this.removeEventListener('pointerover', this.#handlePreloadIntent);
    this.removeEventListener('pointerdown', this.#handlePreloadIntent);
    clearTimeout(this.#intentPreloadTimeout);
    this.#discardVariantResponsePreload();
  }

  #resetDisclosureArrow = () => {
    this.#disclosureArrowActive = false;
  };

  /** Preserve native arrow selection; close only on explicit confirmation.
   * @param {KeyboardEvent} event
   */
  #handleDisclosureKeydown = (event) => {
    if (!(event.target instanceof Element)) return;
    const disclosure = event.target.closest('details.variant-option-disclosure');
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open) return;
    const radio = event.target instanceof HTMLInputElement && event.target.type === 'radio';
    this.#disclosureArrowActive = radio && ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.key);
    if (radio && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      event.target.click();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      event.stopPropagation();
      disclosure.open = false;
      disclosure.querySelector('summary')?.focus();
    }
  };

  /** Native arrows can synthesize clicks; those must not dismiss the options.
   * @param {MouseEvent} event
   */
  #handleDisclosureClick = (event) => {
    if (this.#disclosureArrowActive || !(event.target instanceof HTMLInputElement) || event.target.type !== 'radio') return;
    const disclosure = event.target.closest('details.variant-option-disclosure');
    if (!(disclosure instanceof HTMLDetailsElement) || !disclosure.open) return;
    const option = event.target;
    const confirmationFocus = document.activeElement;
    // Native/AT activation can dispatch change after a click-listener microtask.
    // Wait for that event task to finish before capturing the update to await.
    setTimeout(async () => {
      if (!option.checked || !disclosure.isConnected || !disclosure.open) return;
      const selectionUpdate = this.#selectionUpdate;
      await selectionUpdate;
      const focus = document.activeElement;
      if (
        this.#selectionUpdate !== selectionUpdate ||
        !option.checked || !disclosure.isConnected || !disclosure.open ||
        (focus !== confirmationFocus && !disclosure.contains(focus))
      ) return;
      closeConfirmedDisclosure(disclosure, (summary) => this.#focusConfirmedDisclosure(summary));
    }, 0);
  };

  /** Keep the confirmed control visible even when AT focus did not scroll it.
   * @param {HTMLElement} summary
   */
  #focusConfirmedDisclosure(summary) {
    summary.focus({ preventScroll: true });
    requestAnimationFrame(() => {
      if (!summary.isConnected || document.activeElement !== summary) return;
      const viewport = window.visualViewport;
      let viewportTop = viewport?.offsetTop ?? 0;
      let viewportBottom = viewportTop + (viewport?.height ?? window.innerHeight);
      let scrollContainer = summary.parentElement;
      while (scrollContainer) {
        const overflow = getComputedStyle(scrollContainer).overflowY;
        if (/^(auto|scroll)$/.test(overflow) && scrollContainer.scrollHeight > scrollContainer.clientHeight) break;
        scrollContainer = scrollContainer.parentElement;
      }
      if (scrollContainer) {
        const containerBounds = scrollContainer.getBoundingClientRect();
        const containerTop = containerBounds.top + scrollContainer.clientTop;
        viewportTop = Math.max(viewportTop, containerTop);
        viewportBottom = Math.min(viewportBottom, containerTop + scrollContainer.clientHeight);
      }
      const bounds = summary.getBoundingClientRect();
      const header = document.querySelector('header-component[sticky]');
      const headerBounds = header?.getBoundingClientRect();
      const visibleHeaderBottom = headerBounds && headerBounds.top <= viewportTop && headerBounds.bottom > viewportTop
        ? headerBounds.bottom : viewportTop;
      let top = visibleHeaderBottom + 8;
      const bottom = viewportBottom - 8;
      let distance = 0;
      if (bounds.top < top) {
        // An upward correction can reveal a scroll-up header that was hidden.
        if (header?.getAttribute('sticky') === 'scroll-up' && headerBounds) {
          top = Math.max(top, viewportTop + headerBounds.height + 8);
        }
        distance = bounds.top - top;
      } else if (bounds.bottom > bottom) {
        distance = Math.min(bounds.bottom - bottom, bounds.top - top);
      }
      if (distance) (scrollContainer ?? window).scrollBy({ top: distance, behavior: 'instant' });
    });
  }

  /** Close after focus actually leaves, without redirecting Tab or pointer focus.
   * @param {FocusEvent} event
   */
  #handleDisclosureFocusout = (event) => {
    if (!(event.target instanceof Element) || !(event.relatedTarget instanceof Node)) return;
    const disclosure = event.target.closest('details.variant-option-disclosure');
    // Safari touch can move focus to a focusable product ancestor before the
    // radio click. Closing here removes its target and cancels the selection.
    if (disclosure instanceof HTMLDetailsElement && event.relatedTarget.contains(disclosure)) {
      return;
    }
    if (disclosure instanceof HTMLDetailsElement && !disclosure.contains(event.relatedTarget)) {
      disclosure.open = false;
      this.#resetDisclosureArrow();
    }
  };

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement ? event.target.options[event.target.selectedIndex] : event.target;

    if (!selectedOption) return;

    clearTimeout(this.#intentPreloadTimeout);
    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products, because changing the product
    // might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const newUrl = selectedOption.dataset.connectedProductUrl;
    const loadsNewProduct = isOnProductPage && !!newUrl && newUrl !== currentUrl;
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
      ? 'featured-product-information'
      : undefined;

    const { optionValueId = '', variantId = '', connectedProductUrl = '', optionMediaId: mediaId = '' } =
      selectedOption.dataset;
    this.#selectionUpdate = this.fetchUpdatedSection(this.buildRequestUrl(selectedOption), {
      morphElementSelector,
      detail: { optionValueId, variantId, connectedProductUrl, mediaId },
    });

    const url = new URL(window.location.href);

    if (isOnProductPage) {
      if (variantId) {
        url.searchParams.set('variant', variantId);
      } else {
        url.searchParams.delete('variant');
      }
    }

    // Change the path if the option is connected to another product via combined listing.
    if (loadsNewProduct) {
      url.pathname = newUrl;
    }

    if (url.href !== window.location.href) {
      yieldToMainThread().then(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Preloads only the media and variant response a customer shows intent to select.
   * Hover and focus intent are briefly debounced to avoid loading every option while
   * a pointer or keyboard focus moves across a swatch row.
   *
   * @param {FocusEvent | PointerEvent} event
   */
  #handlePreloadIntent = (event) => {
    if (this.closest('product-card, quick-add-dialog')) return;
    if (!(event.target instanceof Element)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target.closest('[data-option-value-id]') ??
          event.target.closest('.variant-option__button-label')?.querySelector('[data-option-value-id]');
    if (!(selectedOption instanceof HTMLElement)) return;

    clearTimeout(this.#intentPreloadTimeout);

    const preload = () => {
      const mediaId = selectedOption.dataset.optionMediaId;

      if (mediaId) {
        const mediaGallery = /** @type {import('./media-gallery').MediaGallery | null | undefined} */ (
          this.closest('.shopify-section')?.querySelector('media-gallery')
        );
        if (typeof mediaGallery?.preloadMedia === 'function') mediaGallery.preloadMedia(mediaId);
      }

      this.#preloadVariantResponse(selectedOption);
    };

    // Start touch/mouse press intent immediately. Coalesce hover and keyboard focus
    // because their speculative requests can otherwise race across adjacent swatches.
    const delay = event.type === 'pointerdown' ? 0 : event.type === 'focusin' ? 60 : 120;
    this.#intentPreloadTimeout = setTimeout(preload, delay);
  };

  /**
   * Starts one reusable product response for an unselected PDP option.
   * @param {HTMLElement} selectedOption
   */
  #preloadVariantResponse(selectedOption) {
    if (
      this.dataset.templateProductMatch !== 'true' ||
      this.closest('featured-product-information') ||
      selectedOption.dataset.connectedProductUrl ||
      (selectedOption instanceof HTMLInputElement && selectedOption.checked)
    ) {
      return;
    }

    const connection = /** @type {{ saveData?: boolean } | undefined} */ (Reflect.get(navigator, 'connection'));
    if (connection?.saveData) return;

    const requestUrl = this.#buildIntentRequestUrl(selectedOption);
    if (!requestUrl || this.#variantResponsePreload?.url === requestUrl) return;

    this.#discardVariantResponsePreload();

    const controller = new AbortController();
    const promise = fetch(requestUrl, { signal: controller.signal }).then((response) => response.text());
    this.#variantResponsePreload = { url: requestUrl, controller, promise };

    // The selection path consumes the original promise. This handler prevents an
    // abandoned speculative request from surfacing as an unhandled rejection.
    void promise.catch(() => {});
  }

  /**
   * Builds the exact product URL a candidate option would request without changing
   * the current form selection or combined-listing request state.
   * @param {HTMLElement} selectedOption
   * @returns {string | undefined}
   */
  #buildIntentRequestUrl(selectedOption) {
    const productUrl = this.dataset.productUrl;
    const fieldset = selectedOption.closest('fieldset');
    const candidateOptionValueId = selectedOption.dataset.optionValueId;
    if (!productUrl || !fieldset || !candidateOptionValueId) return undefined;

    /** @type {HTMLElement[]} */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));
    let replacedCurrentSelection = false;
    const optionValues = selectedOptions.map((option) => {
      if (option.closest('fieldset') === fieldset) {
        replacedCurrentSelection = true;
        return candidateOptionValueId;
      }

      return option.dataset.optionValueId;
    });

    if (!replacedCurrentSelection || optionValues.some((value) => !value)) return undefined;

    const params = [];
    const viewParamValue = getViewParameterValue();
    if (viewParamValue) params.push(`view=${viewParamValue}`);
    params.push(`option_values=${optionValues.join(',')}`);

    return `${productUrl}?${params.join('&')}`;
  }

  #discardVariantResponsePreload() {
    this.#variantResponsePreload?.controller.abort();
    this.#variantResponsePreload = undefined;
  }

  /**
   * Transfers ownership of an exact speculative response to the selection path.
   * @param {string} requestUrl
   */
  #takeVariantResponsePreload(requestUrl) {
    const preload = this.#variantResponsePreload;
    this.#variantResponsePreload = undefined;

    if (preload?.url === requestUrl) return preload;

    preload?.controller.abort();
    return undefined;
  }

  /**
   * @typedef {object} FieldsetMeasurements
   * @property {HTMLFieldSetElement} fieldset
   * @property {number | undefined} currentIndex
   * @property {number | undefined} previousIndex
   * @property {number | undefined} currentWidth
   * @property {number | undefined} previousWidth
   */

  /**
   * Gets measurements for a single fieldset (read phase).
   * @param {number} fieldsetIndex
   * @returns {FieldsetMeasurements | null}
   */
  #getFieldsetMeasurements(fieldsetIndex) {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const fieldset = fieldsets[fieldsetIndex];
    const checkedIndices = this.#checkedIndices[fieldsetIndex];
    const radios = this.#radios[fieldsetIndex];

    if (!radios || !checkedIndices || !fieldset) return null;

    const [currentIndex, previousIndex] = checkedIndices;

    return {
      fieldset,
      currentIndex,
      previousIndex,
      currentWidth: currentIndex !== undefined ? radios[currentIndex]?.parentElement?.offsetWidth : undefined,
      previousWidth: previousIndex !== undefined ? radios[previousIndex]?.parentElement?.offsetWidth : undefined,
    };
  }

  /**
   * Applies measurements to a fieldset (write phase).
   * @param {FieldsetMeasurements} measurements
   */
  #applyFieldsetMeasurements({ fieldset, currentWidth, previousWidth, currentIndex, previousIndex }) {
    if (currentWidth) {
      fieldset.style.setProperty('--pill-width-current', `${currentWidth}px`);
    } else if (currentIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-current');
    }

    if (previousWidth) {
      fieldset.style.setProperty('--pill-width-previous', `${previousWidth}px`);
    } else if (previousIndex !== undefined) {
      fieldset.style.removeProperty('--pill-width-previous');
    }
  }

  /**
   * Updates the fieldset CSS.
   * @param {number} fieldsetIndex - The fieldset index.
   */
  updateFieldsetCss(fieldsetIndex) {
    if (Number.isNaN(fieldsetIndex)) return;

    const measurements = this.#getFieldsetMeasurements(fieldsetIndex);
    if (measurements) {
      this.#applyFieldsetMeasurements(measurements);
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target - The target element.
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
          }

          this.updateFieldsetCss(fieldsetIndex);

          const selectedValue = fieldset.querySelector('[data-selected-option-value]');
          if (selectedValue) selectedValue.textContent = target.value;
          const disclosureValue = fieldset.closest('.variant-option-disclosure')?.querySelector('[data-disclosure-selected-value]');
          if (disclosureValue) {
            const optionValue = disclosureValue.querySelector('[data-disclosure-option-value]');
            if (optionValue) optionValue.textContent = target.value;
            // Keep the single accessible trigger name in sync with its visible value.
            disclosureValue.closest('summary')?.setAttribute('aria-label', target.dataset.disclosureLabel ?? target.value);
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find((option) => option.value === newValue);

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption - The selected option.
   * @param {string | null} [source] - The source.
   * @param {string[]} [sourceSelectedOptionsValues] - The source selected options values.
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl = selectedOption.dataset.connectedProductUrl || this.#pendingRequestUrl || this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl - The request URL.
   * @param {object} [options] - Request options.
   * @param {string} [options.morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   * @param {{ optionValueId?: string, variantId?: string, connectedProductUrl?: string, mediaId?: string }} [options.detail] - Synchronous product select event detail.
   */
  fetchUpdatedSection(requestUrl, { morphElementSelector, detail = {} } = {}) {
    const { optionValueId = '', variantId, connectedProductUrl = '', mediaId = '' } = detail;
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    const preloadedResponse = this.#takeVariantResponsePreload(requestUrl);
    const requestController = preloadedResponse?.controller ?? new AbortController();
    this.#abortController = requestController;

    const deferredEventPromise = ProductSelectEvent.createPromise();
    const selectedOptions = this.getAllSelectedOptions();

    this.dispatchEvent(
      new ProductSelectEvent({
        product: {
          id: this.dataset.productId ?? '',
          title: this.dataset.productTitle ?? '',
          handle: this.dataset.productHandle ?? '',
        },
        selectedOptions,
        detail: {
          optionValueId,
          variantId,
          connectedProductUrl,
          mediaId,
        },
        promise: deferredEventPromise.promise,
      })
    );

    const responseTextPromise = preloadedResponse
      ? preloadedResponse.promise.catch((error) => {
          if (error.name === 'AbortError') throw error;

          // Retry a failed speculative request only while this is still the active selection.
          if (this.#abortController !== requestController || requestController.signal.aborted) {
            throw new DOMException('The operation was aborted.', 'AbortError');
          }

          const fallbackController = new AbortController();
          this.#abortController = fallbackController;
          return fetch(requestUrl, { signal: fallbackController.signal }).then((response) => response.text());
        })
      : fetch(requestUrl, { signal: requestController.signal }).then((response) => response.text());

    return responseTextPromise
      .then((responseText) => {
        this.#pendingRequestUrl = undefined;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        // Defer is only useful for the initial rendering of the page. Remove it here.
        html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

        const variantPickerJsonScript = html.querySelector(`variant-picker script[type="application/json"]`);
        const textContent = variantPickerJsonScript?.textContent;

        if (!textContent) {
          deferredEventPromise.resolve({
            variant: null,
            detail: {
              html,
              productId: this.dataset.productId ?? '',
              sourceId: this.selectedOptionId,
              resource: null,
            },
          });
          return;
        }

        let newProduct;

        if (morphElementSelector === 'main') {
          this.updateMain(html);
        } else if (morphElementSelector) {
          this.updateElement(html, morphElementSelector);
        } else {
          const { overflowList } = this.refs;
          const wasSwatchesExpanded =
            overflowList instanceof OverflowList && overflowList.getAttribute('disabled') === 'true';

          newProduct = this.updateVariantPicker(html);

          if (wasSwatchesExpanded) {
            const overflowListAfterMorph = overflowList;
            if (overflowListAfterMorph instanceof OverflowList) {
              overflowListAfterMorph.showAll();
            }
          }
        }

        // Resolve the ProductSelectEvent promise with all data needed by listeners
        if (this.selectedOptionId) {
          const variantData = JSON.parse(textContent);

          if (variantData && typeof variantData === 'object') {
            const productViewAttr = variantPickerJsonScript
              ?.closest('[view-event-payload]')
              ?.getAttribute('view-event-payload')
              ?.trim();

            deferredEventPromise.resolve({
              variant: (productViewAttr && JSON.parse(productViewAttr))?.product?.selectedVariant ?? null,
              detail: {
                html,
                productId: this.dataset.productId ?? '',
                newProduct,
                sourceId: this.selectedOptionId,
                resource: variantData,
              },
            });

            return;
          }
        }

        // Variant data is null/invalid (e.g. unavailable variant combination) —
        // still include detail with html so listeners can update UI (disable buttons, morph text)
        deferredEventPromise.resolve({
          variant: null,
          detail: {
            html,
            productId: this.dataset.productId ?? '',
            newProduct,
            sourceId: this.selectedOptionId,
            resource: null,
          },
        });
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          // A newer selection superseded this request. Resolve without update data
          // so ProductSelectEvent listeners can finish without reporting an error.
          deferredEventPromise.resolve({ variant: null });
          return;
        }

        deferredEventPromise.reject(error);
        console.error(error);
      });
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document | Element} newHtml - The new HTML.
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource, {
      ...MORPH_OPTIONS,
      getNodeKey: (node) => {
        if (!(node instanceof HTMLElement)) return undefined;
        const key = node.dataset.key;
        return key;
      },
    });
    this.updateVariantPickerCss();

    return newProduct;
  }

  updateVariantPickerCss() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    // Batch all reads first across all fieldsets to avoid layout thrashing
    const measurements = fieldsets.map((_, index) => this.#getFieldsetMeasurements(index)).filter((m) => m !== null);

    // Batch all writes after all reads
    for (const measurement of measurements) {
      this.#applyFieldsetMeasurements(measurement);
    }
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml - The new HTML.
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml - The new HTML.
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    morph(main, newMain);
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (!(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets all the selected options.
   * @returns {{name: string, value: string}[]} All the currently selected options.
   */
  getAllSelectedOptions() {
    /** @type {{name: string, value: string}[]} */
    const options = [];

    // For <select> elements, use .selectedOptions to get the current selection
    // (the [selected] HTML attribute only reflects the initial state, not user changes)
    for (const select of this.querySelectorAll('select')) {
      const selected = select.selectedOptions[0];
      if (selected?.dataset?.optionName) {
        options.push({ name: selected.dataset.optionName, value: selected.value });
      }
    }

    // For radio/checkbox fieldsets, :checked reflects the current state
    /** @type {NodeListOf<HTMLInputElement>} */
    const checkedInputs = this.querySelectorAll('fieldset input:checked');
    for (const input of checkedInputs) {
      if (input.dataset?.optionName) {
        options.push({ name: input.dataset.optionName, value: input.value });
      }
    }

    return options;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(this.querySelectorAll('select option[selected], fieldset input:checked'));

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
